import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-medium text-fg mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-fg-2 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your personal and health information.
          </p>
          <p className="text-sm text-fg-3 mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Introduction</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              Vegetablah.com ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our health tracking application and website.
            </p>
            <p className="text-fg-2 leading-relaxed">
              By using Vegetablah.com you agree to the collection and use of information in accordance with this policy.
            </p>
          </CardContent>
        </Card>

        {/* Information We Collect */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Information We Collect</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-fg mb-3">Personal Information</h3>
                <ul className="list-disc list-inside text-fg-2 space-y-2">
                  <li>Name and email address</li>
                  <li>Profile picture (optional)</li>
                  <li>Account credentials</li>
                  <li>Contact form submissions</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-fg mb-3">Health and Wellness Data</h3>
                <ul className="list-disc list-inside text-fg-2 space-y-2">
                  <li>Food intake logs and nutritional information</li>
                  <li>Health metrics (weight, mood, energy levels, sleep quality)</li>
                  <li>Personal statistics and correlations</li>
                  <li>Fitness and activity data from connected devices</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-fg mb-3">Usage Information</h3>
                <ul className="list-disc list-inside text-fg-2 space-y-2">
                  <li>Device information and browser type</li>
                  <li>IP address and location data</li>
                  <li>Pages visited and features used</li>
                  <li>Article reading preferences and history</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-fg mb-3">Third-Party Data</h3>
                <ul className="list-disc list-inside text-fg-2 space-y-2">
                  <li>Fitbit account data (when connected)</li>
                  <li>Google authentication data</li>
                  <li>Nutritional database information</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How We Use Your Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside text-fg-2 space-y-2">
              <li>Provide and maintain our health tracking services</li>
              <li>Process and analyze your health data to generate insights and correlations</li>
              <li>Improve our application features and user experience</li>
              <li>Send you important updates about your account and our services</li>
              <li>Respond to your customer support inquiries</li>
              <li>Ensure the security and integrity of our platform</li>
              <li>Comply with legal obligations</li>
            </ul>
          </CardContent>
        </Card>

        {/* Information Sharing */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Information Sharing and Disclosure</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-fg-2 space-y-2">
              <li><strong>With your consent:</strong> When you explicitly agree to share your data</li>
              <li><strong>Service providers:</strong> Trusted third-party services that help us operate (hosting, analytics, email)</li>
              <li><strong>Fitbit integration:</strong> Data shared directly with Fitbit for device connectivity</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-fg-2 leading-relaxed mt-4">
              We never share your health data with advertisers or marketing companies.
            </p>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Data Security</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              We implement comprehensive security measures to protect your information:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-fg mb-2">Technical Security</h4>
                <ul className="list-disc list-inside text-fg-2 space-y-1 text-sm">
                  <li>End-to-end encryption for data transmission</li>
                  <li>Secure password hashing</li>
                  <li>Regular security audits</li>
                  <li>Automated vulnerability scanning</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-fg mb-2">Administrative Security</h4>
                <ul className="list-disc list-inside text-fg-2 space-y-1 text-sm">
                  <li>Access controls and permissions</li>
                  <li>Employee training on data privacy</li>
                  <li>Regular backup procedures</li>
                  <li>Incident response planning</li>
                </ul>
              </div>
            </div>
            <p className="text-fg-2 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure.
              We cannot guarantee absolute security but are committed to protecting your data using industry-standard practices.
            </p>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Data Retention</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this policy:
            </p>
            <ul className="list-disc list-inside text-fg-2 space-y-2">
              <li><strong>Account data:</strong> Retained while your account is active and for a reasonable period after deactivation</li>
              <li><strong>Health logs:</strong> Retained indefinitely unless you request deletion</li>
              <li><strong>Usage analytics:</strong> Aggregated and anonymized data may be retained longer for service improvement</li>
              <li><strong>Legal compliance:</strong> Some data may be retained longer if required by law</li>
            </ul>
            <p className="text-fg-2 leading-relaxed mt-4">
              You can request deletion of your personal data at any time, subject to legal and legitimate business requirements.
            </p>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Your Rights and Choices</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-fg mb-2">Access & Portability</h4>
                <ul className="list-disc list-inside text-fg-2 space-y-1 text-sm">
                  <li>Request access to your data</li>
                  <li>Export your data in readable format</li>
                  <li>Receive data in structured format</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-fg mb-2">Control & Deletion</h4>
                <ul className="list-disc list-inside text-fg-2 space-y-1 text-sm">
                  <li>Correct inaccurate information</li>
                  <li>Delete your account and data</li>
                  <li>Withdraw consent for data processing</li>
                </ul>
              </div>
            </div>
            <p className="text-fg-2 leading-relaxed mt-4">
              To exercise these rights, please contact us using the information provided below. We will respond to your request within 30 days.
            </p>
          </CardContent>
        </Card>

        {/* Cookies and Tracking */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Cookies and Tracking Technologies</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="list-disc list-inside text-fg-2 space-y-2">
              <li><strong>Essential cookies:</strong> Required for basic functionality and security</li>
              <li><strong>Analytics cookies:</strong> Help us understand how you use our service</li>
              <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="text-fg-2 leading-relaxed mt-4">
              You can control cookie preferences through your browser settings. However, disabling certain cookies may affect functionality.
            </p>
          </CardContent>
        </Card>

        {/* Third-Party Services */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Third-Party Services</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed mb-4">
            Vegetablah.com integrates with third-party services:
            </p>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-fg">Fitbit Integration</h4>
                <p className="text-fg-2 text-sm">
                  When you connect your Fitbit account, we access fitness and health data as authorized by you.
                  This data is used solely for providing our health tracking features.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-fg">Google Authentication</h4>
                <p className="text-fg-2 text-sm">
                  We use Google OAuth for secure account creation and login. Google may collect information about your use of their service.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-fg">Email Services</h4>
                <p className="text-fg-2 text-sm">
                  We use third-party email services to send notifications and respond to contact forms.
                  Your email address is only used for the specified purposes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Children's Privacy */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed">
              Vegetablah.com is not intended for children under 13 years of age. We do not knowingly collect personal information
              from children under 13. If we become aware that we have collected personal information from a child under 13,
              we will take steps to delete such information. If you are a parent or guardian and you are aware that your child
              has provided us with personal information, please contact us so that we can take necessary action.
            </p>
          </CardContent>
        </Card>

        {/* International Data Transfers */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence.
              We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards
              to protect your information during international transfers.
            </p>
          </CardContent>
        </Card>

        {/* Changes to This Policy */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Changes to This Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last updated" date. We will also provide notice through our
              application or by email if the changes are material. Your continued use of Vegetablah.com after any
              changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <div className="text-center">
          <Card>
            <CardContent className="py-8">
              <h3 className="text-xl font-medium text-fg mb-4">
                Contact Us About Privacy
              </h3>
              <p className="text-fg-2 mb-6">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-fg-2">
                <p><strong>Email:</strong> support@vegetablah.com</p>
                <p><strong>Address:</strong> 18902 224th Ave Ct E, Orting, WA 98360</p>
              </div>
              <div className="mt-6 space-x-4">
                <a
                  href="/contact"
                  className="inline-block bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Contact Us
                </a>
                <a
                  href="/terms-of-service"
                  className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Terms of Service
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
